/**
 * Sprint UX-1: Toggle global Simples/Avançado + Modo Apresentação.
 * - Default por papel: viewer/operator → simple; admin/owner/superadmin → advanced.
 * - Persistência em localStorage por usuário.
 * - Modo Apresentação: oculta flags, módulos shadow, jargão técnico (uso comercial).
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import type { UserRole } from "@/types/syra";

export type UIMode = "simple" | "advanced";
const ADVANCED_ROLES: UserRole[] = ["owner", "admin", "superadmin"];
const MODE_KEY = "syra:ui-mode";
const PRESENTATION_KEY = "syra:presentation-mode";

function readStored<T extends string>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  const v = window.localStorage.getItem(key);
  return (v as T) ?? fallback;
}

export function useUIMode() {
  const { user } = useAuth();
  const role = user?.role as UserRole | undefined;
  const defaultMode: UIMode = role && ADVANCED_ROLES.includes(role) ? "advanced" : "simple";

  const [mode, setModeState] = useState<UIMode>(() => readStored<UIMode>(MODE_KEY, defaultMode));
  const [presentation, setPresentationState] = useState<boolean>(
    () => readStored<"true" | "false">(PRESENTATION_KEY, "false") === "true",
  );

  // Re-sync default when role changes (first auth load)
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.localStorage.getItem(MODE_KEY)) setModeState(defaultMode);
  }, [defaultMode]);

  // Apply CSS attribute to <html> so styles can react globally
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.dataset.uiMode = mode;
    document.documentElement.dataset.presentation = presentation ? "on" : "off";
  }, [mode, presentation]);

  const setMode = useCallback((m: UIMode) => {
    setModeState(m);
    window.localStorage.setItem(MODE_KEY, m);
  }, []);

  const toggleMode = useCallback(() => setMode(mode === "simple" ? "advanced" : "simple"), [mode, setMode]);

  const setPresentation = useCallback((on: boolean) => {
    setPresentationState(on);
    window.localStorage.setItem(PRESENTATION_KEY, on ? "true" : "false");
  }, []);

  const togglePresentation = useCallback(() => setPresentation(!presentation), [presentation, setPresentation]);

  return {
    mode,
    isSimple: mode === "simple",
    isAdvanced: mode === "advanced",
    setMode,
    toggleMode,
    presentation,
    setPresentation,
    togglePresentation,
  };
}
